import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapView, type MapPin, type MapViewHandle } from "../components/MapView";
import {
  MaBottomNav, MaChipRow, MaCircleButton, MaIcon, MaLiveBadge, MaImageSlot, MaTimePill,
  type Chip,
} from "../components/ui";
import { MA_CATS, type CategoryId } from "@myams/shared";
import { useImportedEvents, useVenues } from "../lib/queries";
import { amsterdamHour, isOpenNow, lateNightNudge } from "../lib/time";

interface PinPreview {
  kind: "venue" | "event";
  id: string;
  pinId: string;
  title: string;
  cat: CategoryId | "all" | null;
  neighborhood: string | null;
  image: string | null;
  description: string | null;
  lat: number;
  lng: number;
  route: string;
}

const CAT_CHIPS: Chip[] = [
  { id: "all",    label: "All" },
  { id: "music",  label: "Music",   live: true },
  { id: "food",   label: "Food" },
  { id: "art",    label: "Art" },
  { id: "market", label: "Markets" },
  { id: "cafe",   label: "Cafés" },
  { id: "outdoor",label: "Outdoor" },
  { id: "hidden", label: "Hidden" },
];

export function MapHome() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const mapRef = useRef<MapViewHandle | null>(null);
  const activeCategory = (params.get("cat") ?? "all") as CategoryId | "all";
  const [selectedPinId, setSelectedPinId] = useState<string | null>(null);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [sheetCollapsed, setSheetCollapsed] = useState(false);
  const amsHour = amsterdamHour();
  const lateNudge = lateNightNudge(amsHour);

  // Real curated venues (seeded, geocoded via Nominatim)
  const { data: venues = [] } = useVenues({
    cat: activeCategory === "all" ? undefined : activeCategory,
  });
  // Real imported events from the cron scraper (geocoded via embedded Maps links)
  const { data: events = [] } = useImportedEvents({
    cat: activeCategory,
    located: true,
    upcoming: true,
    limit: 200,
  });

  const pins = useMemo<MapPin[]>(() => {
    const venuePins: MapPin[] = venues
      .filter((v) => v.lat != null && v.lng != null)
      .map((v) => ({ id: `venue:${v.id}`, title: v.name, lat: v.lat!, lng: v.lng!, cat: v.cat, live: v.live }));
    const eventPins: MapPin[] = events
      .filter((e) => e.lat != null && e.lng != null)
      .map((e) => ({ id: `ev:${e.id}`, title: e.title, lat: e.lat!, lng: e.lng!, cat: e.category ?? null, live: true }));
    return [...venuePins, ...eventPins];
  }, [venues, events]);

  // Resolve the currently-selected pin (if any) to a uniform preview shape.
  const preview = useMemo<PinPreview | null>(() => {
    if (!selectedPinId) return null;
    if (selectedPinId.startsWith("venue:")) {
      const id = selectedPinId.slice("venue:".length);
      const v = venues.find((x) => x.id === id);
      if (!v || v.lat == null || v.lng == null) return null;
      return {
        kind: "venue", id: v.id, pinId: selectedPinId, title: v.name,
        cat: v.cat, neighborhood: v.neigh, image: v.coverUrl ?? null,
        description: v.now || null, lat: v.lat, lng: v.lng,
        route: `/venue/${v.id}`,
      };
    }
    if (selectedPinId.startsWith("ev:")) {
      const id = selectedPinId.slice("ev:".length);
      const e = events.find((x) => x.id === id);
      if (!e || e.lat == null || e.lng == null) return null;
      return {
        kind: "event", id: e.id, pinId: selectedPinId, title: e.title,
        cat: e.category ?? null, neighborhood: e.neighborhood ?? null,
        image: e.imageUrl ?? null, description: e.description ?? null,
        lat: e.lat, lng: e.lng,
        route: `/event/${encodeURIComponent(e.id)}`,
      };
    }
    return null;
  }, [selectedPinId, venues, events]);

  // Pan to the selected pin so it's visible above the preview card.
  // Also auto-expand the sheet — otherwise the preview card would be
  // hidden behind the collapsed lip and the click would feel broken.
  useEffect(() => {
    if (preview) {
      mapRef.current?.flyTo(preview.lng, preview.lat, 15);
      setSheetCollapsed(false);
    }
  }, [preview?.pinId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Clear selection when the category filter changes.
  useEffect(() => { setSelectedPinId(null); }, [activeCategory]);

  // Top 4 live spots for the sheet rail.
  const liveNow = useMemo(() => {
    const fromVenues = venues.filter((v) => v.live).map((v) => ({
      kind: "venue" as const, id: v.id, name: v.name, cat: v.cat, now: v.now, image: v.coverUrl,
    }));
    const fromEvents = events.map((e) => ({
      kind: "event" as const, id: e.id, name: e.title, cat: e.category, now: e.description ?? "", image: e.imageUrl,
    }));
    return [...fromVenues, ...fromEvents].slice(0, 8);
  }, [venues, events]);

  const setCat = (id: string) => setParams(id === "all" ? {} : { cat: id });

  return (
    <div className="ma-screen">
      <div style={{ position: "absolute", inset: 0 }}>
        <MapView
          ref={mapRef}
          pins={pins}
          highlight={selectedPinId}
          fitToPins={pins.length > 0 && pins.length < 50 && !selectedPinId}
          interactive
          onPinClick={(p) => setSelectedPinId(p.id)}
          onMapClick={() => setSelectedPinId(null)}
        />
      </div>

      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, zIndex: 10,
        padding: "calc(env(safe-area-inset-top, 20px) + 34px) 14px 12px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            flex: 1, display: "flex", alignItems: "center", gap: 10,
            padding: "10px 14px",
            background: "var(--ma-cream)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
          }}>
            <MaIcon name="pin" size={16} stroke={1.6} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ma-mono" style={{
                fontSize: 9, letterSpacing: "0.14em",
                textTransform: "uppercase", color: "var(--ma-ink-soft)",
              }}>
                Showing
              </div>
              <div style={{ fontFamily: "var(--ma-body)", fontWeight: 600, fontSize: 13, color: "var(--ma-ink)", marginTop: 1 }}>
                {pins.length} spots · {activeCategory === "all" ? "all categories" : MA_CATS[activeCategory].label}
              </div>
            </div>
            <MaTimePill />
          </div>
          <MaCircleButton icon="list" label="Browse categories" onClick={() => navigate("/discover/categories")} />
        </div>

        <div style={{ marginLeft: -14, marginRight: -14, marginTop: 10 }}>
          <MaChipRow items={CAT_CHIPS} active={activeCategory} onSelect={setCat} />
        </div>

        {lateNudge && !nudgeDismissed && (
          <div style={{
            marginTop: 8, padding: "10px 12px",
            background: "var(--ma-ink)", color: "var(--ma-paper)",
            borderRadius: "var(--ma-card-radius)",
            display: "flex", alignItems: "flex-start", gap: 10,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ma-mono" style={{
                fontSize: 10, letterSpacing: "0.16em",
                textTransform: "uppercase", marginBottom: 2,
                color: "var(--ma-mustard)",
              }}>
                {lateNudge.headline}
              </div>
              <div style={{ fontFamily: "var(--ma-body)", fontSize: 12, lineHeight: 1.35, opacity: 0.88 }}>
                {lateNudge.sub}
              </div>
            </div>
            <button onClick={() => setNudgeDismissed(true)} aria-label="Dismiss" style={{
              background: "transparent", border: "none", padding: 0,
              color: "var(--ma-paper)", opacity: 0.7, cursor: "pointer",
              fontFamily: "var(--ma-mono)", fontSize: 12,
            }}>×</button>
          </div>
        )}
      </div>

      {/* Map controls — zoom + locate me, both wired to MapView's imperative handle */}
      <div style={{
        position: "absolute", right: 14, top: 250, zIndex: 10,
        display: "flex", flexDirection: "column", gap: 8,
      }}>
        <MaCircleButton icon="plus" label="Zoom in" onClick={() => mapRef.current?.zoomIn()} />
        <MaCircleButton icon="compass" label="My location" onClick={() => mapRef.current?.locate()} />
      </div>

      <div style={{
        position: "absolute", left: 0, right: 0, bottom: "calc(env(safe-area-inset-bottom, 0px) + 64px)", zIndex: 20,
        background: "var(--ma-cream)",
        borderTop: "var(--ma-border) solid var(--ma-ink)",
        borderTopLeftRadius: 18, borderTopRightRadius: 18,
        boxShadow: "0 -10px 30px rgba(20,12,4,0.12)",
        // Collapsed shows just the grab-handle (28px) so the map gets
        // back the ~350px the rail was eating. maxHeight (not height)
        // because the card-rail variant can be shorter than 380.
        maxHeight: sheetCollapsed ? 28 : 380,
        overflow: "hidden",
        transition: "max-height 240ms cubic-bezier(.4, 0, .2, 1)",
      }}>
        <button
          type="button"
          onClick={() => setSheetCollapsed((c) => !c)}
          aria-label={sheetCollapsed ? "Expand panel" : "Collapse panel"}
          aria-expanded={!sheetCollapsed}
          style={{
            display: "flex", justifyContent: "center", alignItems: "center",
            width: "100%", padding: "10px 0 6px",
            background: "transparent", border: "none", cursor: "pointer",
          }}
        >
          <div style={{
            width: 44, height: 4, borderRadius: 2,
            background: "var(--ma-ink)",
            opacity: sheetCollapsed ? 0.55 : 0.35,
            transition: "opacity 200ms ease, width 200ms ease",
          }} />
        </button>

        {preview ? (
          <PinPreviewCard preview={preview} onClose={() => setSelectedPinId(null)} onOpen={() => navigate(preview.route)} />
        ) : (
        <>
        <div style={{ padding: "4px 16px 12px", display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <div>
            <div className="ma-mono" style={{
              fontSize: 10, letterSpacing: "0.18em",
              textTransform: "uppercase", color: "var(--ma-brick)",
            }}>
              <span className="ma-pulse-dot" style={{ display: "inline-block", marginRight: 6, verticalAlign: "middle" }} />
              Right now · {liveNow.length} spots
            </div>
            <div className="ma-display" style={{ fontSize: 26, color: "var(--ma-ink)", marginTop: 4 }}>
              {activeCategory === "all" ? "Tonight in Amsterdam" : `Tonight · ${MA_CATS[activeCategory].label}`}
            </div>
          </div>
          <button style={{
            background: "transparent", border: "none",
            fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.1em",
            color: "var(--ma-ink-soft)", textDecoration: "underline", cursor: "pointer",
          }} onClick={() => navigate("/now")}>
            List view
          </button>
        </div>

        <div className="ma-h-scroll" style={{
          overflowX: "auto", overflowY: "hidden",
          padding: "0 16px 18px", display: "flex", gap: 10,
        }}>
          {liveNow.map((it) => {
            const cat = MA_CATS[it.cat ?? "all"] ?? MA_CATS.all;
            return (
              <button
                key={`${it.kind}:${it.id}`}
                onClick={() => {
                  if (it.kind === "venue") navigate(`/venue/${it.id}`);
                  else navigate(`/event/${encodeURIComponent(it.id)}`);
                }}
                style={{
                  flexShrink: 0, width: 220,
                  background: "var(--ma-paper)",
                  border: "var(--ma-border) solid var(--ma-ink)",
                  borderRadius: "var(--ma-card-radius)",
                  overflow: "hidden", padding: 0, cursor: "pointer", textAlign: "left",
                }}
              >
                <div style={{ position: "relative" }}>
                  <MaImageSlot height={90} tone={it.cat === "music" ? "brick" : it.cat === "art" ? "canal" : "mustard"} src={it.image ?? null} category={it.cat ?? null} fallbackInitial={it.name} />
                  <div style={{ position: "absolute", top: 8, left: 8 }}><MaLiveBadge /></div>
                </div>
                <div style={{ padding: "10px 12px 12px" }}>
                  <div className="ma-mono" style={{
                    fontSize: 9, letterSpacing: "0.14em",
                    textTransform: "uppercase", color: cat.color, marginBottom: 3,
                  }}>
                    {cat.label}
                  </div>
                  <div className="ma-display" style={{ fontSize: 16, color: "var(--ma-ink)", marginBottom: 6 }}>
                    {it.name}
                  </div>
                  {it.now && (
                    <div className="ma-mono" style={{ fontSize: 10, color: "var(--ma-ink-soft)", lineHeight: 1.3,
                      display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden" }}>
                      {it.now}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        </>
        )}
      </div>

      <MaBottomNav active="discover" />
    </div>
  );
}

function PinPreviewCard({
  preview, onOpen, onClose,
}: { preview: PinPreview; onOpen: () => void; onClose: () => void }) {
  const cat = MA_CATS[preview.cat ?? "all"] ?? MA_CATS.all;
  const tone: "brick" | "canal" | "mustard" | "pink" =
    preview.cat === "music" ? "brick"
    : preview.cat === "art" ? "canal"
    : preview.cat === "queer" || preview.cat === "cinema" ? "pink"
    : "mustard";

  // If the venue's "Open · until 17:00" hint is clearly stale right now,
  // honestly say so instead of pretending.
  const openNow = isOpenNow(preview.description);
  return (
    <div style={{ padding: "4px 16px 16px" }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 10,
      }}>
        <div className="ma-mono" style={{
          fontSize: 10, letterSpacing: "0.18em",
          textTransform: "uppercase", color: cat.color,
          display: "inline-flex", alignItems: "center", gap: 6,
        }}>
          <span className="ma-pulse-dot" style={{ background: cat.color }} />
          {preview.kind === "venue" ? "Venue" : "Event"} · {cat.label}
        </div>
        <button
          onClick={onClose}
          aria-label="Close preview"
          style={{
            width: 32, height: 32, borderRadius: "50%",
            background: "transparent",
            border: "var(--ma-border) solid var(--ma-ink)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", padding: 0, color: "var(--ma-ink)",
          }}>
          <MaIcon name="close" size={14} stroke={2} />
        </button>
      </div>

      <button
        onClick={onOpen}
        style={{
          display: "flex", gap: 12, width: "100%",
          background: "var(--ma-paper)",
          border: "var(--ma-border) solid var(--ma-ink)",
          borderRadius: "var(--ma-card-radius)",
          padding: 0, overflow: "hidden",
          textAlign: "left", cursor: "pointer",
        }}>
        <div style={{ width: 92, flexShrink: 0 }}>
          <MaImageSlot height={92} tone={tone} src={preview.image} category={preview.cat ?? null} fallbackInitial={preview.title} />
        </div>
        <div style={{ flex: 1, padding: "10px 12px 10px 0", minWidth: 0 }}>
          <div className="ma-display" style={{ fontSize: 17, color: "var(--ma-ink)", lineHeight: 1.1, marginBottom: 4,
            display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden" }}>
            {preview.title}
          </div>
          {preview.neighborhood && (
            <div className="ma-mono" style={{
              fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
              color: "var(--ma-ink-soft)", marginBottom: 4,
            }}>
              {preview.neighborhood}
            </div>
          )}
          {preview.description && (
            <div style={{
              fontFamily: "var(--ma-body)", fontSize: 12,
              color: openNow === false ? "var(--ma-ink-soft)" : "var(--ma-ink-soft)",
              lineHeight: 1.3,
              textDecoration: openNow === false ? "line-through" : "none",
              display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden",
              opacity: openNow === false ? 0.6 : 1,
            }}>
              {preview.description}
            </div>
          )}
          {openNow === false && (
            <div className="ma-mono" style={{
              fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase",
              color: "var(--ma-brick)", marginTop: 4,
            }}>
              closed now · check listed hours
            </div>
          )}
        </div>
      </button>

      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button
          onClick={onOpen}
          style={{
            flex: 2, padding: "12px 0",
            background: "var(--ma-ink)", color: "var(--ma-paper)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.14em",
            textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
          View {preview.kind === "venue" ? "venue" : "event"}
          <MaIcon name="arrow" size={12} stroke={2.4} color="var(--ma-paper)" />
        </button>
      </div>
    </div>
  );
}
